import { DafAnonymousAuthStrategy, DafAuthConfig, DafExchangeStrategy, initRuntime, AuthStrategy } from '@nab/x-runtime';
import { AppConfig } from './AppConfig';
import constants from './constants';
import NabDafInMemoryCacheStrategy from '../components/tokenCacheStrategies/NabDafInMemoryCacheStrategy';
import { IDigitalData } from '@nab/x-types';
import { AnalyticsMgr, DigitalDataStrategy } from '@nab/nab-x-sdk-browser';
import { LaunchDarklyFlagStrategy } from '@nab/nab-x-sdk-browser';
import { checkHashTokenValidity } from './checkTokenValid';
import { CustomNabCAuthStrategy } from 'components/tokenCacheStrategies/CustomNabCAuthStrategy';
import { IWindowADRUM } from '../util/Interfaces';
import { getRumStrategy } from '../components/AppDynamicsStrategies/RUMstrategy';

declare global {
    interface Window {
        digitalData: IDigitalData;
        ADRUM: IWindowADRUM;
    }
}

let initialised: boolean = false;
let appConfig: any;
let authConfig: DafAuthConfig;

const ADRUMStrategy = {
    rum: {
        strategy: getRumStrategy()
    }
};

const authTokenCache = new NabDafInMemoryCacheStrategy();

// Init the strategy
const DDS = new DigitalDataStrategy();
const analytics = new AnalyticsMgr(DDS);

analytics.initPage({
    pageName: window.document.title,
    site: window.location.href
});

/**
 * Gets the tokens(seed / actor token and authenticated token) stored in the cache
 * @returns {Promise<string[]>} tokens in an array
 */
export async function getCachedTokens(): Promise<string[]> {
    const tokens = [];
    try {
        const actorToken = await authTokenCache.getActorToken(authConfig);
        if (actorToken) {
            tokens.push(actorToken);
        }

        const appToken = await authTokenCache.getToken(authConfig);
        if (appToken) {
            tokens.push(appToken.tokenValue);
        }

        const authenticatedToken = window.sessionStorage.getItem(constants.BEARER_TOKEN);
        if (authenticatedToken) {
            tokens.push(authenticatedToken);
        }

        const authenticatedToken_2FA = window.sessionStorage.getItem(constants.AUTHENTICATED_2FA_TOKEN);
        if (authenticatedToken_2FA) {
            tokens.push(authenticatedToken_2FA);
        }

        return tokens;
    } catch (e) {
        return Promise.reject(e);
    }
}

/**
 * Initialises the x-runtime
 *
 * - Assumes that the same environment is used by all mini-apps on a page in non-prod
 * - Sets default auth config and defines auth strategies when the first mini-app is loaded
 * - This config is inherited by all subsequent mini-apps but can be overridden if an app requires
 */
export async function init(config) {
    let tokenUrlFragment = undefined;
    if (initialised && appConfig) {
        return appConfig;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const ssc = urlParams.get(constants.URL_FRAGMENT_SUPER_SECRET_CODE);
    if (ssc) {
        if (window.digitalData?.user) {
            window.digitalData.user.ssc = ssc;
        } else {
            window.digitalData.user = {
                ssc: ssc
            }
        }
    }

    appConfig = new AppConfig(config);
    const userContext = (navigator.cookieEnabled && window.sessionStorage.getItem(constants.USERCONTEXT)) ? window.sessionStorage.getItem(constants.USERCONTEXT) : constants.DEFAULT_USERCONTEXT_VALUE;
    let dafEndPoint = appConfig.shellConfig.kongEsgEndPoint + constants.IDP_OAUTH_TOKEN_ENDPOINT;
    let clientId = appConfig.shellConfig.kongClientId;

    if (userContext === constants.USERCONTEXT_INTERNAL) {
        dafEndPoint = appConfig.shellConfig.internal.dafEndPoint;
        clientId = appConfig.shellConfig.internal.kongClientId;
        authConfig = {
            environment: appConfig.shellConfig.internal.environment, // or whatever non-prod environment you're using
            context: 'internal', // or 'internal'
            tokenType: 'Bearer',
            tokenSource: 'DAF',
            grantType: appConfig.shellConfig.internal.grantType,
            scopes: [appConfig.shellConfig.internal.scope],
            isSeed: appConfig.shellConfig.skipTokenExchange
        };
    } else {
        authConfig = {
            environment: appConfig.shellConfig.environment, // or whatever non-prod environment you're using
            context: 'external', // or 'internal'
            tokenType: 'Bearer',
            tokenSource: 'DAF',
            grantType: appConfig.shellConfig.grantType,
            scopes: [appConfig.shellConfig.scope],
            isSeed: appConfig.shellConfig.skipTokenExchange
        };
    }

    //Clear the token on initialization
    if (appConfig.clearTokenInit) {
        window.sessionStorage.removeItem(constants.BEARER_TOKEN);
        window.sessionStorage.removeItem(constants.AUTHENTICATED_2FA_TOKEN);
    }

    const hash = window.location.hash;
    if (hash.includes(constants.TOKEN_IN_URL)) {
        const urlFragmentArr = hash.split('=');
        if (urlFragmentArr[1]) {
            tokenUrlFragment = decodeURIComponent(urlFragmentArr[1]);
            const tokenValid = await checkHashTokenValidity(dafEndPoint, tokenUrlFragment, clientId);
            if (tokenValid) {
                // Remove any left over bearerTokens
                window.sessionStorage.removeItem(constants.BEARER_TOKEN);
                window.sessionStorage.setItem(constants.BEARER_TOKEN, tokenUrlFragment);
            }
        }
    }

    const authStrategy: Array<AuthStrategy> = [

        // Supports the DafClientCredentialsAuthStrategy for NabC tenant only
        new CustomNabCAuthStrategy(dafEndPoint, clientId, true),

        // Supports the 'nab:anonymous' grant type. Replace this with the appropriate strategy for your shell
        new DafAnonymousAuthStrategy(dafEndPoint, clientId, true),

        // Token exchange strategy for mini-apps
        new DafExchangeStrategy(dafEndPoint)
    ];

    await initRuntime({
        // Default auth config. This will be inherited by all mini-apps within your shell unless explicitly overridden
        authConfig,
        authStrategies: appConfig.authenticationRequired ? authStrategy : [],
        authTokenCacheStrategy: authTokenCache,
        userConfig: {
            org: 'NAB',
            type: 'customer'
        },
        servicesConfig: {
            dataSecurity: {
                iv: {
                    LibraryUrl: appConfig.iovationLib
                },
                bc: {
                    LibraryUrl: appConfig.bioCatchLib
                },
                gbd: {
                    LibraryUrl: appConfig.getBrowserDataLib
                }
            },
            analytics: {
                strategy: DDS
            },
            flags: {
                //Created dummy values for miniapp to override
                strategy: new LaunchDarklyFlagStrategy(appConfig.shellConfig.launchDarlyKey ? appConfig.shellConfig.launchDarlyKey : constants.DEFAULT_LAUNCHDARKLY_KEY),
                apiKey: '',
                initialData: {}
            },
            interaction: {
                endpoint: appConfig?.fraudInteractionEndpoint || '',
            },
            ...ADRUMStrategy
        }
    });

    initialised = true;

    return appConfig;
}
